import { Storybook } from "@tento-ui/ui/storybook";
import { SheetLispExample } from "./sheet-lisp-example.ts";
import { NaiveDate } from "@tento-chrono";
import { RowId } from "@tento-lona/sheets";

/** The storybook page for the sheet Lisp example, for a component gallery. */
export class SheetLispExampleStoryBook extends Storybook.Default {
  title(): string {
    return "Sheet Lisp Example";
  }

  examples(): Storybook.Example[] {
    return [
      {
        title: "Calculate Profit",
        render: () => {
          const $example = SheetLispExample.make();
          $example.bind({
            code: `; Calculate profit
(- (rows[":revenue"]) (rows[":expenses"]))`,
            rows: [
              {
                id: RowId.mock("rid_revenue"),
                label: "Revenue",
                lookupKey: ":revenue",
                timeScale: "week",
                data: [["yw-2024-1", 1000]],
                type: "number",
                children: null,
                attributes: null,
                clientId: null,
                formula: null,
                heightPx: null,
                icon: null,
                position: null,
              },
              {
                id: RowId.mock("rid_expenses"),
                label: "Expenses",
                lookupKey: ":expenses",
                timeScale: "week",
                data: [["yw-2024-1", 600]],
                type: "number",
                children: null,
                attributes: null,
                clientId: null,
                formula: null,
                heightPx: null,
                icon: null,
                position: null,
              },
            ],
            timescale: "week",
            column: NaiveDate.Partial.parse("yw-2024-1").exp(),
          });
          return $example.assignStyles({
            width: "100%",
            maxWidth: "600px",
          });
        },
      },
      {
        title: "Check if Weekday",
        render: () => {
          const $example = SheetLispExample.make();
          $example.bind({
            code: `; Check if Monday-Friday
(if (<= date.dow.0 5)
    "Workday"
    "Weekend")`,
            rows: [
              {
                id: RowId.mock("rid_dummy"),
                label: "Context Row",
                timeScale: "day",
                data: [],
                type: "number",
                lookupKey: null,
                children: null,
                attributes: null,
                clientId: null,
                formula: null,
                heightPx: null,
                icon: null,
                position: null,
              }
            ],
            timescale: "day",
            column: NaiveDate.Partial.parse("ymd-2024-1-8").exp(),
          });
          return $example.assignStyles({
            width: "100%",
            maxWidth: "600px",
          });
        },
      },
      {
        title: "Completion Percentage",
        render: () => {
          const $example = SheetLispExample.make();
          $example.bind({
            code: `; Calculate completion percentage
(* (/ (rows[":completed"]) (rows[":total"])) 100)`,
            rows: [
              {
                id: RowId.mock("rid_completed"),
                label: "Completed Tasks",
                lookupKey: ":completed",
                timeScale: "week",
                data: [["yw-2024-1", 15]],
                type: "number",
                children: null,
                attributes: null,
                clientId: null,
                formula: null,
                heightPx: null,
                icon: null,
                position: null,
              },
              {
                id: RowId.mock("rid_total"),
                label: "Total Tasks",
                lookupKey: ":total",
                timeScale: "week",
                data: [["yw-2024-1", 20]],
                type: "number",
                children: null,
                attributes: null,
                clientId: null,
                formula: null,
                heightPx: null,
                icon: null,
                position: null,
              },
            ],
            timescale: "week",
            column: NaiveDate.Partial.parse("yw-2024-1").exp(),
          });
          return $example.assignStyles({
            width: "100%",
            maxWidth: "600px",
          });
        },
      },
      {
        title: "Conditional Cell Styling",
        render: () => {
          const $example = SheetLispExample.make();
          $example.bind({
            code: `; Set background based on performance
(if (>= (rows[":score"]) 90)
    (cell.bg "#4CAF50")
    (if (>= (rows[":score"]) 70)
        (cell.bg "#FFC107")
        (cell.bg "#F44336")))

(rows[":score"])`,
            rows: [
              {
                id: RowId.mock("rid_score"),
                label: "Performance Score",
                lookupKey: ":score",
                timeScale: "week",
                data: [["yw-2024-1", 85]],
                type: "number",
                children: null,
                attributes: null,
                clientId: null,
                formula: null,
                heightPx: null,
                icon: null,
                position: null,
              },
            ],
            timescale: "week",
            column: NaiveDate.Partial.parse("yw-2024-1").exp(),
          });
          return $example.assignStyles({
            width: "100%",
            maxWidth: "600px",
          });
        },
      },
      {
        title: "Sum Multiple Rows",
        render: () => {
          const $example = SheetLispExample.make();
          $example.bind({
            code: `; Calculate total hours
(+ (rows[":coding"])
   (rows[":meetings"])
   (rows[":planning"]))`,
            rows: [
              {
                id: RowId.mock("rid_coding"),
                label: "Coding Hours",
                lookupKey: ":coding",
                timeScale: "week",
                data: [["yw-2024-1", 25]],
                type: "number",
                children: null,
                attributes: null,
                clientId: null,
                formula: null,
                heightPx: null,
                icon: null,
                position: null,
              },
              {
                id: RowId.mock("rid_meetings"),
                label: "Meeting Hours",
                lookupKey: ":meetings",
                timeScale: "week",
                data: [["yw-2024-1", 10]],
                type: "number",
                children: null,
                attributes: null,
                clientId: null,
                formula: null,
                heightPx: null,
                icon: null,
                position: null,
              },
              {
                id: RowId.mock("rid_planning"),
                label: "Planning Hours",
                lookupKey: ":planning",
                timeScale: "week",
                data: [["yw-2024-1", 5]],
                type: "number",
                children: null,
                attributes: null,
                clientId: null,
                formula: null,
                heightPx: null,
                icon: null,
                position: null,
              },
            ],
            timescale: "week",
            column: NaiveDate.Partial.parse("yw-2024-1").exp(),
          });
          return $example.assignStyles({
            width: "100%",
            maxWidth: "600px",
          });
        },
      },
      {
        title: "Day of Week Name",
        render: () => {
          const $example = SheetLispExample.make();
          $example.bind({
            code: `; Get day name (Mon, Tue, etc)
date.dow`,
            rows: [
              {
                id: RowId.mock("rid_dummy"),
                label: "Context Row",
                timeScale: "day",
                data: [],
                type: "number",
                lookupKey: null,
                children: null,
                attributes: null,
                clientId: null,
                formula: null,
                heightPx: null,
                icon: null,
                position: null,
              }
            ],
            timescale: "day",
            column: NaiveDate.Partial.parse("ymd-2024-1-15").exp(),
          });
          return $example.assignStyles({
            width: "100%",
            maxWidth: "600px",
          });
        },
      },
      {
        title: "Access by Row Label",
        render: () => {
          const $example = SheetLispExample.make();
          $example.bind({
            code: `; Access by label
(* (rows["Daily Rate"]) (rows["Hours Worked"]))`,
            rows: [
              {
                id: RowId.mock("rid_rate"),
                label: "Daily Rate",
                timeScale: "week",
                data: [["yw-2024-1", 150]],
                type: "number",
                lookupKey: null,
                children: null,
                attributes: null,
                clientId: null,
                formula: null,
                heightPx: null,
                icon: null,
                position: null,
              },
              {
                id: RowId.mock("rid_hours"),
                label: "Hours Worked",
                timeScale: "week",
                data: [["yw-2024-1", 8]],
                type: "number",
                lookupKey: null,
                children: null,
                attributes: null,
                clientId: null,
                formula: null,
                heightPx: null,
                icon: null,
                position: null,
              },
            ],
            timescale: "week",
            column: NaiveDate.Partial.parse("yw-2024-1").exp(),
          });
          return $example.assignStyles({
            width: "100%",
            maxWidth: "600px",
          });
        },
      },
      {
        title: "Define Functions",
        render: () => {
          const $example = SheetLispExample.make();
          $example.bind({
            code: `; Define custom BMI function
(define (bmi weight-kg height-m)
  (/ weight-kg (* height-m height-m)))

(bmi (rows[":weight"]) (rows[":height"]))`,
            rows: [
              {
                id: RowId.mock("rid_weight"),
                label: "Weight (kg)",
                lookupKey: ":weight",
                timeScale: "week",
                data: [["yw-2024-1", 70]],
                type: "number",
                children: null,
                attributes: null,
                clientId: null,
                formula: null,
                heightPx: null,
                icon: null,
                position: null,
              },
              {
                id: RowId.mock("rid_height"),
                label: "Height (m)",
                lookupKey: ":height",
                timeScale: "week",
                data: [["yw-2024-1", 1.75]],
                type: "number",
                children: null,
                attributes: null,
                clientId: null,
                formula: null,
                heightPx: null,
                icon: null,
                position: null,
              },
            ],
            timescale: "week",
            column: NaiveDate.Partial.parse("yw-2024-1").exp(),
          });
          return $example.assignStyles({
            width: "100%",
            maxWidth: "600px",
          });
        },
      },
    ];
  }
}
